import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { api } from 'src/client/api';
import { setOneTrustData } from '../actions/app';
import { logClientError, logClientInfo } from '../actions/logging';
import { jurisdictions } from '../consent/constants/geoRules';
import { type ResolvedGeoRule, resolveGeoRule } from '../consent/geoRule';
import { ENABLE_ONETRUST_DEBUG_LOGS } from '../constants/experiments/config';
import { selectGdprApplies, selectOneTrustGeoRule } from '../selectors/app';
import { selectCountryCode, selectCountryStateCode } from '../selectors/config';
import useActions from './useActions';

interface ConsentLog {
  message: string;
  context: Record<string, unknown>;
}

interface ConsentJurisdictionActions {
  setOneTrustData: (oneTrustData: { geoRule: ResolvedGeoRule }) => void;
  logClientError: (logData: ConsentLog) => void;
  logClientInfo: (logData: ConsentLog) => void;
}

/**
 * Resolves which privacy regime governs the session and puts it in the store, for the `privacy`
 * block of the Unified Events common context.
 *
 * OneTrust exposes no API for the geolocation rule it applied, so we evaluate their published rule
 * set against the Platform's country and state ourselves. Two independent signals are compared
 * against that result rather than used in place of it — the rule OneTrust actually applied, and
 * the TCF API's `gdprApplies` — because only our own resolution is guaranteed not to drift from
 * the location we hand OneTrust.
 *
 * Those comparisons log behind the OneTrust debug-log setting. A systematic disagreement would
 * otherwise be one log line per session, and it already shows up unmissably in the reporting
 * itself: an unresolved rule leaves every regime marked not applicable.
 */
export default function useConsentJurisdiction(): void {
  const countryCode: string | undefined = useSelector(selectCountryCode);
  const stateCode: string | undefined = useSelector(selectCountryStateCode);
  const geoRule: ResolvedGeoRule | undefined = useSelector(selectOneTrustGeoRule);
  const gdprApplies: boolean | undefined = useSelector(selectGdprApplies);

  const actions = useActions<ConsentJurisdictionActions>({
    setOneTrustData,
    logClientError,
    logClientInfo,
  });

  const logInfo = useCallback(
    (logData: ConsentLog) => {
      if (api.getConfigSetting<boolean>(ENABLE_ONETRUST_DEBUG_LOGS)) {
        actions.logClientInfo(logData);
      }
    },
    [actions],
  );

  useEffect(() => {
    if (!countryCode || geoRule) {
      return;
    }

    resolveGeoRule({ countryCode, stateCode })
      .then((rule) => {
        if (!rule) {
          logInfo({
            message: 'ONE_TRUST.GEO_RULE.UNMATCHED',
            context: { countryCode, stateCode },
          });
          return;
        }

        if (!rule.jurisdiction) {
          logInfo({
            message: 'ONE_TRUST.GEO_RULE.UNKNOWN_TYPE',
            context: { id: rule.id, name: rule.name, type: rule.type },
          });
        }

        // A rule was added or re-scoped in the OneTrust dashboard without our matching following
        // it. Both ids are reported so the dashboard change can be identified from the log alone.
        if (rule.appliedId && rule.appliedId !== rule.id) {
          logInfo({
            message: 'ONE_TRUST.GEO_RULE.APPLIED_MISMATCH',
            context: { resolvedId: rule.id, appliedId: rule.appliedId, countryCode, stateCode },
          });
        }

        actions.setOneTrustData({ geoRule: rule });
      })
      .catch((error: unknown) => {
        actions.logClientError({
          message: 'ONE_TRUST.GEO_RULE.RESOLUTION_FAILED',
          context: { countryCode, stateCode, error },
        });
      });
  }, [actions, countryCode, geoRule, logInfo, stateCode]);

  useEffect(() => {
    const jurisdiction = geoRule?.jurisdiction;

    if (typeof gdprApplies !== 'boolean' || !jurisdiction) {
      return;
    }

    // The CMP's own answer to "is this session under GDPR". Agreement with the rule we resolved is
    // the strongest confirmation available that our matching is right.
    if (gdprApplies !== (jurisdiction === jurisdictions.gdpr)) {
      logInfo({
        message: 'ONE_TRUST.GEO_RULE.GDPR_APPLIES_MISMATCH',
        context: { gdprApplies, jurisdiction, geoRuleName: geoRule?.name },
      });
    }
  }, [gdprApplies, geoRule, logInfo]);
}
